import {
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import React, { useCallback, useState } from "react";
import { FieldErrors } from "../form/FieldErrors";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { emptyValueOnUndefined } from "../../utils/fields";
import EmailGroup, { EmailGroupItem } from "../../models/email-groups";
import UserSelect from "../user/UserSelect";
import EmailGroupSelect from "./EmailGroupSelect";
import User from "../../models/user";

interface EmailGroupItemFormProps {
  value: EmailGroupItem;
  initialUser?: User;
  initialEmailGroup?: EmailGroup;
  disabled?: boolean;
  onDelete: (_: EmailGroupItem) => void;
  onChange: (_: EmailGroupItem) => void;
  errors: Errors;
}

const EmailGroupItemForm: React.FC<EmailGroupItemFormProps> = ({
  value,
  disabled,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState<EmailGroupItem>(value);

  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onUserChange = useCallback(
    (newUser: User | null) => {
      const newItem: EmailGroupItem = {
        ...editingItem,
        user: newUser ? newUser : undefined,
        userId: newUser ? newUser.id : undefined,
      };

      newItem.email = newUser !== null ? undefined : editingItem.email;
      newItem.emailGroup =
        newUser !== null ? undefined : editingItem.emailGroup;
      newItem.groupId = newUser !== null ? undefined : editingItem.groupId;

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onGroupChange = useCallback(
    (newGroup: EmailGroup | null) => {
      const newItem: EmailGroupItem = {
        ...editingItem,
        emailGroup: newGroup ? newGroup : undefined,
        groupId: newGroup ? newGroup.id : undefined,
      };

      newItem.email = newGroup !== null ? undefined : editingItem.email;
      newItem.user = newGroup !== null ? undefined : editingItem.user;
      newItem.userId = newGroup !== null ? undefined : editingItem.userId;

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const newItem: EmailGroupItem = {
        ...editingItem,
        email: newValue !== null ? newValue : undefined,
        user: undefined,
        emailGroup: undefined,
        userId: undefined,
        groupId: undefined,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const newItem: EmailGroupItem = {
        ...editingItem,
        type: newValue !== null ? newValue : undefined,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  return (
    <div className="section border rounded mb-2 p-2">
      <CFormGroup>
        <CRow className={"mt-2"}>
          <CCol md={2}>
            <CLabel className={"mt-2"}>Tipo de Entrada:</CLabel>
          </CCol>
          <CCol md={3}>
            <CSelect
              type="text"
              value={editingItem.type}
              onChange={onTypeChange}
            >
              <option value={"email"}>Email</option>
              <option value={"user"}>Usuario</option>
              <option value={"group"}>Grupo de correo</option>
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("type", errors) as string[]}
            ></FieldErrors>
          </CCol>
          {editingItem.type == "email" && (
            <>
              <CCol md={2} className={"mt-2"}>
                Dirección Email:
              </CCol>
              <CCol md={5}>
                <CInput
                  type="text"
                  value={emptyValueOnUndefined(editingItem.email)}
                  onChange={onEmailChange}
                  placeholder="Dirección de correo"
                ></CInput>
                <FieldErrors
                  errors={getFieldErrors("email", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </>
          )}
          {editingItem.type == "user" && (
            <>
              <CCol md={2} className={"mt-2"}>
                Usuario:
              </CCol>
              <CCol md={5}>
                <UserSelect
                  value={editingItem.user ? editingItem.user : null}
                  onChange={onUserChange}
                ></UserSelect>
                <FieldErrors
                  errors={getFieldErrors("userId", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </>
          )}

          {editingItem.type == "group" && (
            <>
              <CCol md={2} className={"mt-2"}>
                Grupo de Correo:
              </CCol>
              <CCol md={5}>
                <EmailGroupSelect
                  value={editingItem.emailGroup ? editingItem.emailGroup : null}
                  onChange={onGroupChange}
                ></EmailGroupSelect>
                <FieldErrors
                  errors={getFieldErrors("groupId", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </>
          )}
        </CRow>
      </CFormGroup>

      <CFormGroup>
        <CRow>
          <CCol>
            <CButton
              className="btn btn-danger float-right"
              onClick={onDeleteClick}
              disabled={disabled}
            >
              <i className="fa fa-trash"></i>
            </CButton>
          </CCol>
        </CRow>
      </CFormGroup>
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: EmailGroupItemFormProps,
  nextItemProps: EmailGroupItemFormProps
): boolean => {
  const areEqual =
    prevItemProps.value.id === nextItemProps.value.id &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors);

  return areEqual;
};

export default React.memo(EmailGroupItemForm, propsAreEqual);
